import React, { useContext, useState, useEffect, useRef } from 'react';
import AuthContext from '../../../authentication/AuthContext';
import { Button, Form, FormControl, InputGroup, Table } from 'react-bootstrap';
import axios from 'axios';
import { useAlert } from 'react-alert';
import OrderRow from './OrderRow';
import moment from 'moment';
import UserContext from '../../../user/UserContext';
import LoadingSpinner from '../../../common/LoadingSpinner';
import DatePicker from 'react-datepicker';
import { faCalendarAlt, faRedo, faSearch } from '@fortawesome/free-solid-svg-icons';

import { useLocation, useHistory } from 'react-router-dom';
import { IconWrapper } from '../../../common/IconWrapper';

export default function Orders() {
    const [loading, setLoading] = useState(true);
    const [orders, setOrders] = useState([{ name: 'All Suppliers', code: '' }]);
    const [errorState, setErrorState] = useState(false);
    const [startDate, setStartDate] = useState(
        moment()
            .subtract(14, 'days')
            .toDate()
    );
    const [endDate, setEndDate] = useState(moment().toDate());
    const [status, setStatus] = useState('');
    const [suppliers, setSuppliers] = useState([{ name: 'All Suppliers', code: '' }]);
    const [selectedSupplier, setSelectedSupplier] = useState('');
    const [searchTerm, setSearchTerm] = useState('');

    const cas = useContext(AuthContext);

    const alert = useAlert();

    const fromDateRef = useRef(null);
    const toDateRef = useRef(null);

    const { userDetails } = useContext(UserContext);

    const location = useLocation();
    const history = useHistory();

    const getSuppliersForFranchise = () => {
        if (userDetails.currentFranchise) {
            axios
                .get(window.CONFIG['ws.activoc-static-data-api.suppliers'], {
                    headers: {
                        Authorization: cas,
                        'Content-Type': 'application/json'
                    },
                    params: {
                        franchiseCode: userDetails.currentFranchise.code
                    }
                })
                .then(response => {
                    const json = response.data;
                    addCustomSupplierOptions(json);
                    setSuppliers(json);
                })
                .catch(() => alert.error('ERROR: Unable to retrieve suppliers for this franchise.'));
        }
    };

    const addCustomSupplierOptions = supplierList => {
        supplierList.unshift({ name: 'All Suppliers', code: '' });
        supplierList.push({ name: 'Bestway', code: 'BWAY1' });
    };

    const retrieveOrders = params => {
        setLoading(true);
        if (userDetails.currentFranchise) {
            let url = params.searchTerm
                ? window.CONFIG['ws.activoc-web-order-api.orders.searchQuery']
                : window.CONFIG['ws.activoc-web-order-api.orders.search'];

            axios
                .get(url, {
                    params: params,
                    headers: {
                        Authorization: cas,
                        'Content-Type': 'application/json'
                    }
                })
                .then(response => {
                    let json = response.data;
                    if (json.length > 0) {
                        setOrders(json.map(order => <OrderRow order={order} key={order.id} />));
                        setLoading(false);
                    } else {
                        setOrders(
                            <tr className={'error-row'}>
                                <td colSpan={9}>No Orders Available</td>
                            </tr>
                        );
                        setErrorState(true);
                        alert.info('No Orders Found');
                        setLoading(false);
                    }
                })
                .catch(error => {
                    setOrders(
                        <tr className={'error-row'}>
                            <td colSpan={9}>No search results were returned</td>
                        </tr>
                    );
                    if (error.response) {
                        setLoading(false);
                        return;
                    }
                    setErrorState(true);
                    alert.error('ERROR: Unable to retrieve orders.');
                    setLoading(false);
                });
        }
    };

    const retrieveOrdersNoSearch = () => {
        retrieveOrders({
            franchise: userDetails.currentFranchise ? userDetails.currentFranchise.code : '',
            fromDate: moment()
                .subtract(14, 'days')
                .format('YYYY-MM-DD'),
            toDate: moment().format('YYYY-MM-DD'),
            status: undefined,
            supplierCode: undefined
        });
    };

    const resetFilters = () => {
        history.push('/', {});
    };

    useEffect(() => {
        getSuppliersForFranchise();
    }, [userDetails, cas]);

    useEffect(() => {
        if (location.state && location.state.searchParams) {
            const params = location.state.searchParams;
            if (params.searchTerm) {
                setSearchTerm(params.searchTerm);
            } else {
                setStartDate(moment(params.fromDate).toDate());
                setEndDate(moment(params.toDate).toDate());
                setStatus(params.status);
                setSelectedSupplier(params.supplierCode);
            }
            retrieveOrders(location.state.searchParams);
        } else {
            retrieveOrdersNoSearch();
        }
        // eslint-disable-next-line
    }, [userDetails, cas, location]);

    return (
        <div className={'content'}>
            <div className={'order-filters-container'}>
                <p>Use filters and search to find your orders:</p>
                <Form
                    className={'order-filters flex'}
                    onSubmit={e => {
                        e.preventDefault();
                        let params =
                            searchTerm.length > 0
                                ? {
                                      franchise: userDetails.currentFranchise.code,
                                      searchTerm: searchTerm
                                  }
                                : {
                                      franchise: userDetails.currentFranchise.code,
                                      fromDate: moment(startDate).format('YYYY-MM-DD'),
                                      toDate: moment(endDate).format('YYYY-MM-DD'),
                                      status: status === '' ? undefined : status,
                                      supplierCode: selectedSupplier === '' ? undefined : selectedSupplier
                                  };
                        history.push('/', { searchParams: params });
                    }}
                >
                    <InputGroup size={'lg'}>
                        <InputGroup.Prepend onClick={() => fromDateRef.current.setOpen(true)}>
                            <InputGroup.Text>From:</InputGroup.Text>
                        </InputGroup.Prepend>
                        <DatePicker
                            className={'form-control form-control-lg'}
                            selected={startDate}
                            onChange={date => setStartDate(date)}
                            dateFormat={'dd/MM/yyyy'}
                            autoComplete={'off'}
                            ref={fromDateRef}
                            maxDate={moment.min([moment(), moment(endDate)]).toDate()}
                        />
                        <InputGroup.Append
                            onClick={() => fromDateRef.current.setOpen(true)}
                            data-testid={'from-calendar-icon'}
                        >
                            <InputGroup.Text>
                                <IconWrapper icon={faCalendarAlt} />
                            </InputGroup.Text>
                        </InputGroup.Append>
                    </InputGroup>
                    <InputGroup size={'lg'}>
                        <InputGroup.Prepend onClick={() => toDateRef.current.setOpen(true)}>
                            <InputGroup.Text>To:</InputGroup.Text>
                        </InputGroup.Prepend>
                        <DatePicker
                            className={'form-control form-control-lg'}
                            selected={endDate}
                            onChange={date => setEndDate(date)}
                            dateFormat={'dd/MM/yyyy'}
                            autoComplete={'off'}
                            ref={toDateRef}
                            maxDate={moment().toDate()}
                            minDate={startDate}
                        />
                        <InputGroup.Append
                            onClick={() => toDateRef.current.setOpen(true)}
                            data-testid={'to-calendar-icon'}
                        >
                            <InputGroup.Text>
                                <IconWrapper icon={faCalendarAlt} />
                            </InputGroup.Text>
                        </InputGroup.Append>
                    </InputGroup>
                    <FormControl
                        as={'select'}
                        value={status}
                        onChange={event => setStatus(event.target.value)}
                        size={'lg'}
                        data-testid={'status'}
                    >
                        <option value={''}>Any Status</option>
                        <option value={'OPEN'}>Open</option>
                        <option value={'SUBMITTED'}>Submitted</option>
                        <option value={'DELIVERED'}>Delivered</option>
                        <option value={'INVOICED'}>Invoiced</option>
                        <option value={'DELETED'}>Deleted</option>
                    </FormControl>
                    <FormControl
                        as={'select'}
                        value={selectedSupplier}
                        onChange={e => setSelectedSupplier(e.target.value)}
                        size={'lg'}
                        data-testid={'supplier-dropdown'}
                    >
                        {suppliers.map(supplier => (
                            <option value={supplier.code}>{supplier.name}</option>
                        ))}
                    </FormControl>
                    <FormControl
                        type={'text'}
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        placeholder={'Search by Order No / Ref...'}
                        size={'lg'}
                        className={'search-term'}
                    />
                    <Button variant={'primary'} size={'lg'} type={'submit'} title={'Search'}>
                        <IconWrapper icon={faSearch} /> Search
                    </Button>
                    <Button
                        variant={'primary'}
                        onClick={resetFilters}
                        size={'lg'}
                        data-testid={'clear-search'}
                        title={'Reset'}
                    >
                        <IconWrapper icon={faRedo} flip={'horizontal'} />
                    </Button>
                </Form>
            </div>
            <Table hover={!errorState}>
                <thead>
                    <tr>
                        <th>Supplier</th>
                        <th>Order No.</th>
                        <th>Reference</th>
                        <th>Submitted</th>
                        <th>Delivery</th>
                        <th className={'right'}>Lines</th>
                        <th className={'right'}>Cases</th>
                        <th className={'right'}>Cost</th>
                        <th>Status</th>
                    </tr>
                </thead>
                <tbody>
                    {loading ? (
                        <tr>
                            <td colSpan={9}>
                                <LoadingSpinner />
                            </td>
                        </tr>
                    ) : (
                        orders
                    )}
                </tbody>
            </Table>
        </div>
    );
}
